import type { SeaState } from '@sea/world';
import type { VRCalibration } from '../game/LocalPlayer';

export interface HudHandlers {
  onMotion(value: number): void;
  onSpeed(value: number): void;
  onSea(value: SeaState): void;
  onSeed(value: number): void;
  onSit(): void;
  onChat(text: string): void;
  /** VR 身高校准：站好后调用 */
  onCalibrate(): void;
  /** VR 校准滑块变化 */
  onVRCalibration(cal: Partial<VRCalibration>): void;
  /** 重置校准 */
  onVRCalibrationReset(): void;
  /** 点底栏的语音按钮（开麦/静音/请求权限） */
  onVoiceToggle(): void;
  /** 背景音开关/音量 */
  onBgm(patch: {
    ocean?: boolean;
    music?: boolean;
    oceanVolume?: number;
    musicVolume?: number;
  }): void;
}

export interface HudStats {
  fps: number;
  distance: number;
  islands: number;
  seated: boolean;
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (!node) throw new Error(`HUD 缺少元素 #${id}`);
  return node as T;
}

/**
 * HUD：M1 仍然只服务「一个人对着屏幕调世界」的场景。
 * 到 VR 里它会换成手腕 3D 菜单（DOM 在沉浸式会话里不可交互）。
 */
export function initHud(handlers: HudHandlers) {
  const panel = el<HTMLDivElement>('panel');
  const hint = el<HTMLDivElement>('hint');

  const motion = el<HTMLInputElement>('motion');
  const motionV = el<HTMLOutputElement>('motion-v');
  const speed = el<HTMLInputElement>('speed');
  const speedV = el<HTMLOutputElement>('speed-v');
  const sea = el<HTMLSelectElement>('sea');
  const seed = el<HTMLInputElement>('seed');
  const sit = el<HTMLButtonElement>('sit');

  const fpsV = el<HTMLSpanElement>('fps');
  const distV = el<HTMLSpanElement>('dist');
  const islandsV = el<HTMLSpanElement>('islands');
  const postureV = el<HTMLSpanElement>('posture');
  const roomCodeV = el<HTMLSpanElement>('room-code');
  const roomStateV = el<HTMLSpanElement>('room-state');
  const copyBtn = el<HTMLButtonElement>('copy-link');
  const calibrateBtn = el<HTMLButtonElement>('calibrate');
  const voiceV = el<HTMLButtonElement>('voice-toggle');
  const chatLog = el<HTMLDivElement>('chat-log');
  const chatForm = el<HTMLFormElement>('chat-form');
  const chatInput = el<HTMLInputElement>('chat-input');

  /* ---- VR 校准滑块 ---- */
  const calInputs = {
    scale: el<HTMLInputElement>('cal-scale'),
    reach: el<HTMLInputElement>('cal-reach'),
    wristDeg: el<HTMLInputElement>('cal-wrist'),
    tiltDeg: el<HTMLInputElement>('cal-tilt'),
    yawDeg: el<HTMLInputElement>('cal-yaw'),
    curlFlip: el<HTMLInputElement>('cal-flip'),
    elbowFlip: el<HTMLInputElement>('cal-elbow'),
    headFlip: el<HTMLInputElement>('cal-head'),
    offsetZ: el<HTMLInputElement>('cal-z'),
    offsetX: el<HTMLInputElement>('cal-x'),
    offsetY: el<HTMLInputElement>('cal-y'),
  };
  const calOutputs = {
    scale: el<HTMLElement>('cal-scale-v'),
    reach: el<HTMLElement>('cal-reach-v'),
    wristDeg: el<HTMLElement>('cal-wrist-v'),
    tiltDeg: el<HTMLElement>('cal-tilt-v'),
    yawDeg: el<HTMLElement>('cal-yaw-v'),
    curlFlip: el<HTMLElement>('cal-flip-v'),
    elbowFlip: el<HTMLElement>('cal-elbow-v'),
    headFlip: el<HTMLElement>('cal-head-v'),
    offsetZ: el<HTMLElement>('cal-z-v'),
    offsetX: el<HTMLElement>('cal-x-v'),
    offsetY: el<HTMLElement>('cal-y-v'),
  };
  const calReset = el<HTMLButtonElement>('cal-reset');

  const formatCal = (key: keyof typeof calInputs, value: number): string => {
    if (key === 'wristDeg' || key === 'tiltDeg' || key === 'yawDeg') return `${Math.round(value)}°`;
    return value.toFixed(2);
  };

  const readCal = (): Partial<VRCalibration> => ({
    scale: Number(calInputs.scale.value),
    reach: Number(calInputs.reach.value),
    wristDeg: Number(calInputs.wristDeg.value),
    tiltDeg: Number(calInputs.tiltDeg.value),
    yawDeg: Number(calInputs.yawDeg.value),
    curlFlip: calInputs.curlFlip.checked,
    elbowFlip: calInputs.elbowFlip.checked,
    headFlip: calInputs.headFlip.checked,
    offsetZ: Number(calInputs.offsetZ.value),
    offsetX: Number(calInputs.offsetX.value),
    offsetY: Number(calInputs.offsetY.value),
  });

  const NUMERIC_CAL_KEYS = (Object.keys(calInputs) as Array<keyof typeof calInputs>).filter(
    (k) => k !== 'curlFlip' && k !== 'elbowFlip' && k !== 'headFlip',
  );

  const defaults: VRCalibration = {
    scale: 1.1,
    reach: 1.5,
    wristDeg: 20,
    tiltDeg: 60,
    yawDeg: 180,
    curlFlip: true,
    elbowFlip: false,
    headFlip: false,
    offsetX: 0,
    offsetY: 0.2,
    offsetZ: 0,
  };

  const setCalInputs = (cal: VRCalibration): void => {
    calInputs.curlFlip.checked = cal.curlFlip;
    calOutputs.curlFlip.textContent = cal.curlFlip ? '反' : '正';
    calInputs.elbowFlip.checked = cal.elbowFlip;
    calOutputs.elbowFlip.textContent = cal.elbowFlip ? '反' : '正';
    for (const key of NUMERIC_CAL_KEYS) {
      calInputs[key].value = String(cal[key]);
      calOutputs[key].textContent = formatCal(key, cal[key]);
    }
  };

  calInputs.elbowFlip.addEventListener('change', () => {
    calOutputs.elbowFlip.textContent = calInputs.elbowFlip.checked ? '反' : '正';
    handlers.onVRCalibration(readCal());
  });

  calInputs.curlFlip.addEventListener('change', () => {
    calOutputs.curlFlip.textContent = calInputs.curlFlip.checked ? '反' : '正';
    handlers.onVRCalibration(readCal());
  });

  for (const key of NUMERIC_CAL_KEYS) {
    calInputs[key].addEventListener('input', () => {
      const cal = readCal();
      calOutputs[key].textContent = formatCal(key, Number(calInputs[key].value));
      handlers.onVRCalibration(cal);
    });
  }

  calReset.addEventListener('click', () => {
    setCalInputs(defaults);
    handlers.onVRCalibrationReset();
  });

  motion.addEventListener('input', () => {
    motionV.textContent = `${motion.value}%`;
    handlers.onMotion(Number(motion.value) / 100);
  });

  speed.addEventListener('input', () => {
    speedV.textContent = Number(speed.value).toFixed(1);
    handlers.onSpeed(Number(speed.value));
  });

  sea.addEventListener('change', () => {
    handlers.onSea(sea.value as SeaState);
  });

  seed.addEventListener('change', () => {
    const v = Number.parseInt(seed.value, 10);
    if (Number.isFinite(v)) handlers.onSeed(v);
  });

  sit.addEventListener('click', () => handlers.onSit());

  calibrateBtn?.addEventListener('click', () => handlers.onCalibrate());

  copyBtn?.addEventListener('click', () => {    const code = copyBtn.dataset.room ?? '';
    const link = `${location.origin}${location.pathname}?room=${code}`;
    void navigator.clipboard?.writeText(link).then(
      () => {
        copyBtn.textContent = '已复制';
        setTimeout(() => (copyBtn.textContent = '复制邀请链接'), 1500);
      },
      () => {
        copyBtn.textContent = link;
      },
    );
  });

  window.addEventListener('keydown', (e) => {
    if (e.key.toLowerCase() === 'h') {
      const hidden = panel.style.display === 'none';
      panel.style.display = hidden ? '' : 'none';
      hint.style.opacity = hidden ? '1' : '0';
    }
  });

  setTimeout(() => {
    hint.style.opacity = '0';
  }, 7000);

  let accum = 0;
  let frames = 0;
  let lastSeated: boolean | null = null;

  // 聊天：回车发送；发送后清空并保持焦点
  chatForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = chatInput.value;
    chatInput.value = '';
    handlers.onChat(text);
  });

  // 打字的时候不要把按键当成游戏操作（WASD 会被输入框吃掉，但 C/F 需要拦）
  chatInput.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') chatInput.blur();
    e.stopPropagation();
  });

  /* ---- 面板收起 / 展开 ---- */
  // 手机端小屏时面板会铺满整行，必须有办法收起来
  const panelToggle = document.getElementById('panel-toggle') as HTMLButtonElement | null;
  const COLLAPSE_KEY = 'sea.hud.collapsed';

  const applyCollapsed = (collapsed: boolean, persist: boolean): void => {
    if (!panel) return;
    panel.classList.toggle('collapsed', collapsed);
    if (panelToggle) {
      panelToggle.textContent = collapsed ? '+' : '−';
      panelToggle.setAttribute('aria-label', collapsed ? '展开面板' : '收起面板');
    }
    if (persist) {
      try {
        localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
      } catch {
        /* 隐私模式忽略 */
      }
    }
    // canvas 尺寸不随面板变化，但布局变化后让相机比例刷新一下
    window.dispatchEvent(new Event('resize'));
  };

  const togglePanel = (): void => {
    if (!panel) return;
    applyCollapsed(!panel.classList.contains('collapsed'), true);
  };

  if (panelToggle) panelToggle.addEventListener('click', togglePanel);

  // 初始状态：优先用存档；没存档且是小屏（手机）→ 默认收起
  let initialCollapsed = false;
  try {
    const saved = localStorage.getItem(COLLAPSE_KEY);
    if (saved === '1' || saved === '0') initialCollapsed = saved === '1';
    else initialCollapsed = window.matchMedia('(max-width: 640px)').matches;
  } catch {
    initialCollapsed = window.matchMedia('(max-width: 640px)').matches;
  }
  applyCollapsed(initialCollapsed, false);

  /* ---- 底栏语音按钮 ---- */
  voiceV.addEventListener('click', () => handlers.onVoiceToggle());

  /* ---- 背景音 ---- */
  const bgmOceanVol = el<HTMLInputElement>('bgm-ocean-vol');
  const bgmOceanVolV = el<HTMLElement>('bgm-ocean-vol-v');
  const bgmMusicVol = el<HTMLInputElement>('bgm-music-vol');
  const bgmMusicVolV = el<HTMLElement>('bgm-music-vol-v');
  const bgmOcean = el<HTMLInputElement>('bgm-ocean');
  const bgmMusic = el<HTMLInputElement>('bgm-music');

  const emitBgm = (): void => {
    bgmOceanVolV.textContent = `${Math.round(Number(bgmOceanVol.value))}%`;
    bgmMusicVolV.textContent = `${Math.round(Number(bgmMusicVol.value))}%`;
    handlers.onBgm({
      ocean: bgmOcean.checked,
      music: bgmMusic.checked,
      oceanVolume: Number(bgmOceanVol.value) / 100,
      musicVolume: Number(bgmMusicVol.value) / 100,
    });
  };

  bgmOceanVol.addEventListener('input', emitBgm);
  bgmMusicVol.addEventListener('input', emitBgm);
  bgmOcean.addEventListener('change', emitBgm);
  bgmMusic.addEventListener('change', emitBgm);

  const setBgm = (state: {
    ocean: boolean;
    music: boolean;
    oceanVolume: number;
    musicVolume: number;
  }): void => {
    bgmOcean.checked = state.ocean;
    bgmMusic.checked = state.music;
    bgmOceanVol.value = String(Math.round(state.oceanVolume * 100));
    bgmMusicVol.value = String(Math.round(state.musicVolume * 100));
    bgmOceanVolV.textContent = `${Math.round(state.oceanVolume * 100)}%`;
    bgmMusicVolV.textContent = `${Math.round(state.musicVolume * 100)}%`;
  };

  return {
    /** 收起/展开 2D 面板 */
    togglePanel,
    /** 同步背景音开关的 UI 状态 */
    setBgm,
    /** 往聊天记录里追加一条（最多保留 6 条） */
    addChat(text: string, mine: boolean): void {
      const line = document.createElement('div');
      if (mine) line.className = 'mine';
      line.textContent = mine ? `我：${text}` : `对方：${text}`;
      chatLog.appendChild(line);
      while (chatLog.childElementCount > 6) chatLog.firstElementChild?.remove();
      chatLog.scrollTop = chatLog.scrollHeight;
    },

    /** 语音状态文案 */
    setVoice(text: string): void {
      voiceV.textContent = text;
      // 「通话中」用绿色高亮，其余保持普通样式
      voiceV.classList.toggle('on', text.includes('通话中'));
    },

    /** 把校准值同步到滑块（读档 / 重置时用） */
    setVRCalibration(cal: VRCalibration): void {
      setCalInputs(cal);
    },

    /** 房间号 + 连接状态 */
    setRoom(code: string, state: string): void {
      roomCodeV.textContent = code;
      roomStateV.textContent = state;
      copyBtn.dataset.room = code;
    },

    /** 每帧喂一次，内部节流到 4 Hz 再写 DOM（避免 layout 抖动） */
    setStats(stats: HudStats, dt: number): void {
      if (stats.seated !== lastSeated) {
        lastSeated = stats.seated;
        postureV.textContent = stats.seated ? '坐' : '站';
      }

      accum += dt;
      frames++;
      if (accum < 0.25) return;
      const measured = frames / accum;
      const fps = stats.fps > 0 ? stats.fps : measured;
      accum = 0;
      frames = 0;
      fpsV.textContent = `${Math.round(fps)} fps`;
      distV.textContent = `${Math.round(stats.distance)} m`;
      islandsV.textContent = `${stats.islands} 岛`;
    },
  };
}
